import React, { useState } from 'react';
import { Menu, MenuItem, ListItemIcon, ListItemText, CircularProgress } from '@mui/material';
import Button from './Button';
import Icon from './Icon';

export interface RowAction {
  /** Stable identity; defaults to the label when it is a string. */
  key?: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  /** `danger` for destructive actions, `positive` for restoring ones. */
  tone?: 'default' | 'danger' | 'positive';
  disabled?: boolean;
  /** Replaces the icon with a spinner and disables the item. */
  loading?: boolean;
  hidden?: boolean;
  onClick: () => void;
}

interface RowActionMenuProps {
  actions: RowAction[];
  /** Accessible name for the trigger, e.g. "Actions for C001". */
  label?: string;
  /** Called when the menu opens — some pages track which row is active. */
  onOpen?: () => void;
  disabled?: boolean;
}

const TONE_COLOR: Record<NonNullable<RowAction['tone']>, string | undefined> = {
  default: undefined,
  danger: 'var(--neg)',
  positive: 'var(--pos)',
};

/**
 * The ⋮ menu at the end of a table row. One component for every list, so the
 * trigger, the menu's placement and the destructive-action colour are the
 * same everywhere. The trigger stops click propagation, so it can sit inside
 * a row that navigates on click.
 */
const RowActionMenu: React.FC<RowActionMenuProps> = ({ actions, label = 'Row actions', onOpen, disabled }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const visible = actions.filter(a => !a.hidden);
  if (visible.length === 0) return null;

  const close = () => setAnchorEl(null);

  return (
    <>
      <Button
        iconOnly
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={anchorEl ? true : undefined}
        disabled={disabled}
        onClick={e => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
          onOpen?.();
        }}
      >
        <Icon name="more" size={16} />
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        onClick={e => e.stopPropagation()}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{ paper: { sx: { minWidth: 160, mt: 0.5 } } }}
      >
        {visible.map((a, i) => {
          const color = TONE_COLOR[a.tone ?? 'default'];
          return (
            <MenuItem
              key={a.key ?? (typeof a.label === 'string' ? a.label : i)}
              disabled={a.disabled || a.loading}
              onClick={() => {
                close();
                a.onClick();
              }}
              sx={{ fontSize: 13, py: 1.25 }}
            >
              {(a.icon || a.loading) && (
                <ListItemIcon sx={{ color }}>
                  {a.loading ? <CircularProgress size={16} /> : a.icon}
                </ListItemIcon>
              )}
              <ListItemText sx={{ color }} primaryTypographyProps={{ fontSize: 13 }}>
                {a.label}
              </ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default RowActionMenu;
